'use client';

/**
 * Register Form Component
 * Magento 2 production-style create account form with fieldset sections
 */

import { useState } from 'react';
import Link from 'next/link';
import { meetsPasswordPolicy, PASSWORD_REQUIREMENTS_MESSAGE } from '@/lib/password-policy';
import { PasswordRequirements } from '@/components/auth/PasswordRequirements';

type FormStep = 'register' | 'verify';

export function RegisterForm() {
  const [step, setStep] = useState<FormStep>('register');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!firstName.trim()) {
      setError('First Name is required.');
      return;
    }

    if (!lastName.trim()) {
      setError('Last Name is required.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (!meetsPasswordPolicy(password)) {
      setError(PASSWORD_REQUIREMENTS_MESSAGE);
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          firstName: firstName || undefined,
          lastName: lastName || undefined,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      if (data.userConfirmed) {
        setSuccess('Account created! You can now sign in.');
      } else {
        setStep('verify');
        setSuccess('Please check your email for a verification code.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!verificationCode.trim()) {
      setError('Please enter the verification code.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          code: verificationCode,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Verification failed');
      }

      setSuccess('Email verified! You can now sign in.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError(null);
    setSuccess(null);
    setIsResending(true);

    try {
      const response = await fetch('/api/auth/resend-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend code');
      }

      setSuccess('A new verification code has been sent to your email.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend code');
    } finally {
      setIsResending(false);
    }
  };

  if (step === 'verify') {
    return (
      <div className="register-form-wrapper">
        <form onSubmit={handleVerify}>
          {error && (
            <div className="account-alert account-alert--warning mb-4">
              {error}
            </div>
          )}

          {success && (
            <div className="account-alert account-alert--success mb-4">
              {success}
            </div>
          )}

          <fieldset className="register-fieldset">
            <legend className="register-fieldset-legend">
              <span>Email Verification</span>
            </legend>

            <p className="register-fieldset-note">
              We sent a verification code to <strong>{email}</strong>. Please enter
              it below to verify your account.
            </p>

            <div className="register-form-group">
              <label htmlFor="code">
                Verification Code <span className="required">*</span>
              </label>
              <input
                id="code"
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Enter 6-digit code"
                required
                disabled={isLoading}
                autoComplete="one-time-code"
              />
            </div>
          </fieldset>

          <div className="register-actions">
            <button
              type="submit"
              className="register-submit-btn"
              disabled={isLoading || isResending}
            >
              {isLoading ? 'Verifying...' : 'Verify Email'}
            </button>
          </div>

          <div className="register-links">
            <button
              type="button"
              onClick={handleResendCode}
              disabled={isLoading || isResending}
              className="register-resend-link"
            >
              {isResending ? 'Sending...' : "Didn't receive a code? Resend"}
            </button>
            <span className="register-link-separator">|</span>
            <Link href="/login" className="register-signin-link">
              Already verified? Sign in
            </Link>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div className="register-form-wrapper">
      <form onSubmit={handleRegister}>
        {error && (
          <div className="account-alert account-alert--warning mb-4">
            {error}
          </div>
        )}

        {success && (
          <div className="account-alert account-alert--success mb-4">
            {success}
          </div>
        )}

        {/* Personal Information */}
        <fieldset className="register-fieldset">
          <legend className="register-fieldset-legend">
            <span>Personal Information</span>
          </legend>

          <div className="register-form-group">
            <label htmlFor="register-firstname">
              First Name <span className="required">*</span>
            </label>
            <input
              id="register-firstname"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="given-name"
            />
          </div>

          <div className="register-form-group">
            <label htmlFor="register-lastname">
              Last Name <span className="required">*</span>
            </label>
            <input
              id="register-lastname"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="family-name"
            />
          </div>
        </fieldset>

        {/* Sign-in Information */}
        <fieldset className="register-fieldset">
          <legend className="register-fieldset-legend">
            <span>Sign-in Information</span>
          </legend>

          <div className="register-form-group">
            <label htmlFor="register-email">
              Email <span className="required">*</span>
            </label>
            <input
              id="register-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="email"
            />
          </div>

          <div className="register-form-group">
            <label htmlFor="register-password">
              Password <span className="required">*</span>
            </label>
            <input
              id="register-password"
              aria-describedby="register-password-rules"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="new-password"
            />
            <PasswordRequirements password={password} id="register-password-rules" />
          </div>

          <div className="register-form-group">
            <label htmlFor="register-confirm-password">
              Confirm Password <span className="required">*</span>
            </label>
            <input
              id="register-confirm-password"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={isLoading}
              autoComplete="new-password"
            />
          </div>

          <div className="register-checkbox-group">
            <input
              id="register-show-password"
              type="checkbox"
              checked={showPassword}
              onChange={(e) => setShowPassword(e.target.checked)}
            />
            <label htmlFor="register-show-password">Show Password</label>
          </div>

          <p className="register-required-note">* Required Fields</p>
        </fieldset>

        <div className="register-actions">
          <button
            type="submit"
            className="register-submit-btn"
            disabled={isLoading}
          >
            {isLoading ? 'Creating account...' : 'Create an Account'}
          </button>
        </div>

        <div className="register-links">
          <Link href="/login" className="register-signin-link">
            Already have an account? Sign in
          </Link>
        </div>
      </form>
    </div>
  );
}
